/**
 * Billiards (8-Ball) Game - Main Container Component
 *
 * Orchestrates: mode selection -> engine init -> aiming -> shot execution ->
 * physics simulation -> turn management -> game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { BilliardsEngine } from '../BilliardsEngine';
import { BilliardsAI } from '../BilliardsAI';
import { BilliardsCanvas } from './BilliardsCanvas';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { BilliardsStatisticsService } from '../services/BilliardsStatisticsService';
import { BilliardsPersistence } from '../services/BilliardsPersistence';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import {
  BilliardsGameSettings,
  BilliardsGameState,
  BilliardsPlayer,
  BilliardsPlayerType,
  Shot,
  BilliardsGameProps,
  BilliardsDifficulty,
} from '../types';
import './Billiards.css';

type GameView = 'mode-select' | 'playing' | 'game-over';
type GamePhase = 'waiting' | 'aiming' | 'simulating' | 'processing' | 'completed';

const GAME_MODE_CONFIG: GameModeConfig = {
  gameName: '8-Ball Pool',
  gameIcon: '🎱',
  availableModes: ['ai', 'local', 'online'],
};

const BilliardsInner: React.FC<BilliardsGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<BilliardsGameSettings | null>(null);
  const [gameState, setGameState] = useState<BilliardsGameState | null>(null);
  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [statusMessage, setStatusMessage] = useState('');

  // Refs
  const engineRef = useRef<BilliardsEngine>(new BilliardsEngine());
  const aiRef = useRef<BilliardsAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const simulationCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentShotRef = useRef<Shot | null>(null);
  const processResultsRef = useRef<() => void>(() => {});

  // Services
  const statsServiceRef = useRef<BilliardsStatisticsService | null>(null);
  const persistenceRef = useRef<BilliardsPersistence>(new BilliardsPersistence());

  // Initialize stats service
  useEffect(() => {
    const profileManager = new ProfileManager();
    const statsManager = new StatisticsManager(profileManager);
    statsServiceRef.current = new BilliardsStatisticsService(statsManager);
  }, []);

  // Handle mode selection
  const handleModeSelection = useCallback((selection: GameModeSelection) => {
    const modeMap: Record<string, BilliardsGameSettings['mode']> = {
      'ai': 'singleplayer',
      'local': 'local-multiplayer',
      'online': 'multiplayer-realtime',
    };

    const newSettings: BilliardsGameSettings = {
      mode: modeMap[selection.mode] || 'singleplayer',
      aiDifficulty: (selection.difficulty as BilliardsDifficulty) || 'medium',
    };

    setSettings(newSettings);
    startGame(newSettings);
  }, []);

  const startGame = (gameSettings: BilliardsGameSettings) => {
    const engine = engineRef.current;

    const playerTypes: Record<BilliardsPlayer, BilliardsPlayerType> = {
      player1: 'human',
      player2: gameSettings.mode === 'singleplayer' ? 'ai' : 'human',
    };

    engine.initializeGame(playerTypes);

    // Set up AI if needed
    if (gameSettings.mode === 'singleplayer' && gameSettings.aiDifficulty) {
      aiRef.current = new BilliardsAI(gameSettings.aiDifficulty);
    } else {
      aiRef.current = null;
    }

    setGameState(engine.getGameState());
    setView('playing');
    setGamePhase('aiming');
    setStatusMessage('Player 1: Break shot! Aim and shoot.');

    // Track session
    gameSessionIdRef.current = gameSessionManager.current.startGameSession('billiards');
  };

  // Handle shot
  const handleShot = useCallback((shot: Shot) => {
    if (gamePhase !== 'aiming') return;

    const engine = engineRef.current;
    currentShotRef.current = shot;

    // Execute the shot
    engine.executeShot(shot);
    setGamePhase('simulating');
    setStatusMessage('');

    // Start polling for physics completion
    simulationCheckRef.current = setInterval(() => {
      const physics = engine.getPhysics();
      if (physics.areAllStopped()) {
        if (simulationCheckRef.current) {
          clearInterval(simulationCheckRef.current);
          simulationCheckRef.current = null;
        }
        processResultsRef.current();
      }
    }, 50);
  }, [gamePhase]);

  const processResults = () => {
    const engine = engineRef.current;
    const shot = currentShotRef.current;
    if (!shot) return;

    const result = engine.processResults(shot);

    const newState = engine.getGameState();
    setGameState(newState);

    // Auto-save
    if (settings) {
      persistenceRef.current.autoSave(settings.mode, newState, {
        aiDifficulty: settings.aiDifficulty,
        startedAt: newState.startedAt,
        lastSavedAt: Date.now(),
      });
    }

    if (result.gameOver) {
      setGamePhase('completed');
      setView('game-over');

      const winnerText = result.winner === 'player1' ? 'Player 1' : 'Player 2';
      setStatusMessage(`${winnerText} wins!`);

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Over',
        message: `${winnerText} wins the game!`,
        duration: 5000,
      });

      persistenceRef.current.clearAutoSave();
      return;
    }

    if (result.foul) {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Foul!',
        message: result.foulReason || 'Foul committed',
        duration: 3000,
      });
    }

    if (result.assignmentMade) {
      const assignment1 = newState.player1Assignment;
      const assignment2 = newState.player2Assignment;
      addNotification({
        type: NotificationType.INFO,
        title: 'Assignments Made',
        message: `Player 1: ${assignment1}, Player 2: ${assignment2}`,
        duration: 4000,
      });
    }

    // Set up next turn
    if (newState.ballInHand) {
      setStatusMessage(`${newState.currentTurn === 'player1' ? 'Player 1' : 'Player 2'}: Ball in hand - click to place cue ball`);
      setGamePhase('aiming');
    } else {
      const currentPlayer = newState.currentTurn === 'player1' ? 'Player 1' : 'Player 2';
      setStatusMessage(`${currentPlayer}'s turn`);
      setGamePhase('aiming');

      // Schedule AI shot if it's AI's turn
      if (newState.playerTypes[newState.currentTurn] === 'ai' && aiRef.current) {
        setGamePhase('processing');
        setStatusMessage('AI is thinking...');
        aiRef.current.scheduleShot(engine, newState.currentTurn, (aiShot) => {
          // Set phase to aiming so handleShot accepts it
          setGamePhase('aiming');
          handleShot(aiShot);
        });
      }
    }
  };

  // Keep ref up-to-date
  processResultsRef.current = processResults;

  // Handle ball-in-hand placement
  const handlePlaceCueBall = useCallback((x: number, y: number) => {
    const engine = engineRef.current;
    const success = engine.placeCueBall(x, y);

    if (success) {
      setGameState(engine.getGameState());
      const currentPlayer = engine.getCurrentPlayer() === 'player1' ? 'Player 1' : 'Player 2';
      setStatusMessage(`${currentPlayer}'s turn - Aim and shoot`);

      // If AI turn, schedule shot
      const state = engine.getGameState();
      if (state.playerTypes[state.currentTurn] === 'ai' && aiRef.current) {
        setGamePhase('processing');
        setStatusMessage('AI is thinking...');
        aiRef.current.scheduleShot(engine, state.currentTurn, (aiShot) => {
          setGamePhase('aiming');
          handleShot(aiShot);
        });
      }
    } else {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Invalid Position',
        message: 'Cannot place cue ball there - overlaps with another ball.',
        duration: 2000,
      });
    }
  }, [addNotification, handleShot]);

  // New game
  const handleNewGame = () => {
    if (aiRef.current) {
      aiRef.current.destroy();
    }
    if (simulationCheckRef.current) {
      clearInterval(simulationCheckRef.current);
    }
    setView('mode-select');
    setGameState(null);
    setGamePhase('waiting');
    setStatusMessage('');
  };

  // Exit game
  const handleExit = () => {
    if (aiRef.current) {
      aiRef.current.destroy();
    }
    if (simulationCheckRef.current) {
      clearInterval(simulationCheckRef.current);
    }
    if (onExit) {
      onExit();
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (aiRef.current) {
        aiRef.current.destroy();
      }
      if (simulationCheckRef.current) {
        clearInterval(simulationCheckRef.current);
      }
    };
  }, []);

  // Render pocketed balls display
  const renderPocketedBalls = () => {
    if (!gameState) return null;

    return (
      <div className="billiards-pocketed-balls">
        <div className="billiards-pocketed-player">
          <span className="billiards-pocketed-label">
            Player 1 ({gameState.player1Assignment !== 'unassigned' ? gameState.player1Assignment : '?'}):
          </span>
          <div className="billiards-pocketed-list">
            {gameState.player1PocketedBalls.map((ball) => (
              <span
                key={ball.id}
                className="billiards-pocketed-ball"
                title={`Ball ${ball.number}`}
              >
                {ball.number}
              </span>
            ))}
          </div>
        </div>
        <div className="billiards-pocketed-player">
          <span className="billiards-pocketed-label">
            Player 2 ({gameState.player2Assignment !== 'unassigned' ? gameState.player2Assignment : '?'}):
          </span>
          <div className="billiards-pocketed-list">
            {gameState.player2PocketedBalls.map((ball) => (
              <span
                key={ball.id}
                className="billiards-pocketed-ball"
                title={`Ball ${ball.number}`}
              >
                {ball.number}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="billiards-game">
      <div className="billiards-header">
        <button className="billiards-back-btn" onClick={handleExit} aria-label="Back to hub">
          ← Back
        </button>
        <h1 className="billiards-title">🎱 8-Ball Pool</h1>
        {view === 'playing' && (
          <button className="billiards-new-game-btn" onClick={handleNewGame}>
            New Game
          </button>
        )}
      </div>

      {view === 'mode-select' && (
        <UniversalGameModeSelector
          config={GAME_MODE_CONFIG}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
        />
      )}

      {view === 'playing' && gameState && (
        <div className="billiards-play-area">
          {statusMessage && (
            <div className="billiards-status">
              <span className="billiards-status-text">{statusMessage}</span>
              {gameState.tableOpen && (
                <span className="billiards-status-badge">Table Open</span>
              )}
            </div>
          )}

          <BilliardsCanvas
            gameState={gameState}
            physics={engineRef.current.getPhysics()}
            isSimulating={gamePhase === 'simulating'}
            isAiming={gamePhase === 'aiming'}
            ballInHand={gameState.ballInHand}
            onShot={handleShot}
            onPlaceCueBall={handlePlaceCueBall}
            disabled={gamePhase === 'processing' || gamePhase === 'completed'}
          />

          {renderPocketedBalls()}
        </div>
      )}

      {view === 'game-over' && gameState && (
        <div className="billiards-game-over">
          <div className="billiards-game-over-card">
            <h2 className="billiards-game-over-title">Game Over!</h2>
            <p className="billiards-game-over-message">{statusMessage}</p>
            {gameState.gameStatus.type === 'win' && (
              <p className="billiards-game-over-reason">
                {gameState.gameStatus.reason}
              </p>
            )}
            {gameState.gameStatus.type === 'loss' && (
              <p className="billiards-game-over-reason">
                {gameState.gameStatus.reason}
              </p>
            )}
            <div className="billiards-game-over-actions">
              <button className="billiards-btn billiards-btn--primary" onClick={handleNewGame}>
                Play Again
              </button>
              <button className="billiards-btn billiards-btn--secondary" onClick={handleExit}>
                Back to Hub
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const BilliardsGame: React.FC<BilliardsGameProps> = (props) => {
  return (
    <ErrorBoundary>
      <BilliardsInner {...props} />
    </ErrorBoundary>
  );
};
